import { create } from 'zustand';
import api from '@/services/api';
import { identityCheck } from '@/features/a0/identity/identity';
import type {
  CaseStudyGenerationInput,
  CaseStudyListItem,
  CaseStudyDetailResponse,
  CampaignRunResponse,
  WorkspaceKeyword,
} from '@/types';

interface CaseStudyStore {
  // State
  list: CaseStudyListItem[];
  current: CaseStudyDetailResponse | null;
  campaignResult: CampaignRunResponse | null; // last "added to calendar" payload
  isLoadingList: boolean;
  isLoadingCurrent: boolean;
  isGenerating: boolean;
  isRunningCampaign: boolean;
  error: string | null;
  currentWorkspaceId: string | null;
  availableKeywords: WorkspaceKeyword[];
  selectedKeywords: string[];
  isLoadingKeywords: boolean;

  // Actions
  fetchList: (workspaceId?: string) => Promise<void>;
  generate: (input: CaseStudyGenerationInput, workspaceId?: string) => Promise<CaseStudyDetailResponse | null>;
  openCaseStudy: (id: string, workspaceId?: string) => Promise<void>;
  runCampaign: (id: string, workspaceId?: string, platforms?: string[]) => Promise<CampaignRunResponse | null>;
  setCurrentWorkspaceId: (workspaceId: string | null) => void;
  clearCurrent: () => void;
  clearError: () => void;
  reset: () => void;
  fetchKeywords: (workspaceId?: string) => Promise<void>;
  addCustomKeyword: (term: string, workspaceId?: string) => Promise<void>;
  removeCustomKeyword: (term: string, workspaceId?: string) => Promise<void>;
  toggleKeyword: (term: string) => void;
  setSelectedKeywords: (terms: string[]) => void;
}

export const useCaseStudyStore = create<CaseStudyStore>((set, get) => ({
  list: [],
  current: null,
  campaignResult: null,
  isLoadingList: false,
  isLoadingCurrent: false,
  isGenerating: false,
  isRunningCampaign: false,
  error: null,
  currentWorkspaceId: null,
  availableKeywords: [],
  selectedKeywords: [],
  isLoadingKeywords: false,

  fetchList: async (workspaceId?: string) => {
    const live = identityCheck();
    set({ isLoadingList: true, error: null });
    try {
      const list = await api.listCaseStudies(workspaceId);
      if (!live()) return;
      set({ list, currentWorkspaceId: workspaceId || null, isLoadingList: false });
    } catch (err: any) {
      if (!live()) return;
      const message = err?.response?.data?.detail || 'Failed to load case studies';
      set({ error: message, isLoadingList: false });
    }
  },

  generate: async (input: CaseStudyGenerationInput, workspaceId?: string) => {
    const live = identityCheck();
    set({ isGenerating: true, error: null, campaignResult: null });
    try {
      const created = await api.generateCaseStudy(input, workspaceId);
      if (!live()) return null;
      // The generate response has no campaign entries yet — present it as a detail.
      const detail: CaseStudyDetailResponse = { ...created, campaign_entries: [] };
      set({ current: detail, isGenerating: false });
      // Refresh the list so the new case study appears in the sidebar.
      get().fetchList(workspaceId);
      return detail;
    } catch (err: any) {
      if (!live()) return null;
      const message = err?.response?.data?.detail || 'Failed to generate case study';
      set({ error: message, isGenerating: false });
      return null;
    }
  },

  openCaseStudy: async (id: string, workspaceId?: string) => {
    const live = identityCheck();
    set({ isLoadingCurrent: true, error: null, campaignResult: null });
    try {
      const detail = await api.getCaseStudy(id, workspaceId);
      if (!live()) return;
      set({ current: detail, isLoadingCurrent: false });
    } catch (err: any) {
      if (!live()) return;
      const message = err?.response?.data?.detail || 'Failed to open case study';
      set({ error: message, isLoadingCurrent: false });
    }
  },

  runCampaign: async (id: string, workspaceId?: string, platforms?: string[]) => {
    const live = identityCheck();
    set({ isRunningCampaign: true, error: null });
    try {
      const result = await api.runCaseStudyCampaign(id, workspaceId, platforms);
      if (!live()) return null;
      // Reflect the new entries on the currently open case study.
      const current = get().current;
      if (current && current.id === id) {
        set({ current: { ...current, campaign_entries: result.entries } });
      }
      set({ campaignResult: result, isRunningCampaign: false });
      // Refresh the list so the campaign_count badge updates.
      get().fetchList(workspaceId);
      return result;
    } catch (err: any) {
      if (!live()) return null;
      const message = err?.response?.data?.detail || 'Failed to generate campaign';
      set({ error: message, isRunningCampaign: false });
      return null;
    }
  },

  setCurrentWorkspaceId: (workspaceId: string | null) => {
    if (workspaceId !== get().currentWorkspaceId) {
      set({ currentWorkspaceId: workspaceId, list: [], current: null, campaignResult: null });
    }
  },

  fetchKeywords: async (workspaceId?: string) => {
    const live = identityCheck();
    set({ isLoadingKeywords: true });
    try {
      const keywords = await api.getWorkspaceKeywords(workspaceId);
      if (!live()) return;
      set({ availableKeywords: keywords, isLoadingKeywords: false });
    } catch {
      if (!live()) return;
      set({ isLoadingKeywords: false });
    }
  },

  addCustomKeyword: async (term: string, workspaceId?: string) => {
    const live = identityCheck();
    try {
      const keywords = await api.addWorkspaceKeyword(term, workspaceId);
      if (!live()) return;
      set((s) => ({
        availableKeywords: keywords,
        selectedKeywords: s.selectedKeywords.includes(term)
          ? s.selectedKeywords
          : [...s.selectedKeywords, term],
      }));
    } catch {
      /* silently ignore — the chip pool is best-effort */
    }
  },

  removeCustomKeyword: async (term: string, workspaceId?: string) => {
    const live = identityCheck();
    try {
      const keywords = await api.removeWorkspaceKeyword(term, workspaceId);
      if (!live()) return;
      set((s) => ({
        availableKeywords: keywords,
        selectedKeywords: s.selectedKeywords.filter((t) => t !== term),
      }));
    } catch {
      /* silently ignore */
    }
  },

  toggleKeyword: (term: string) => {
    set((s) => ({
      selectedKeywords: s.selectedKeywords.includes(term)
        ? s.selectedKeywords.filter((t) => t !== term)
        : [...s.selectedKeywords, term],
    }));
  },

  setSelectedKeywords: (terms: string[]) => set({ selectedKeywords: terms }),

  clearCurrent: () => set({ current: null, campaignResult: null, selectedKeywords: [] }),
  clearError: () => set({ error: null }),
  reset: () =>
    set({
      list: [],
      current: null,
      campaignResult: null,
      isLoadingList: false,
      isLoadingCurrent: false,
      isGenerating: false,
      isRunningCampaign: false,
      error: null,
      currentWorkspaceId: null,
      availableKeywords: [],
      selectedKeywords: [],
      isLoadingKeywords: false,
    }),
}));
